// ImageUploadService.ts
import { BeforeUploadResponseDTO } from "@/api/PSApiOperations"
import { store } from "../store"
import { throttled } from "@/utils/throttle"
import { atom } from "jotai"
import { Sentry } from "@/telemetry/sentry"
import {
	BASE_NETWORK_RETRY_DELAY_MS,
	isBrowserOffline,
	isNetworkConnectivityError,
} from "@/api/network-behavior"
import type { EncodedJPEGData } from "@/core/encoders/jpeg-encoder"
import { isAbortError } from "@/utils/error"
import { wait } from "@/utils/wait"

type UploadMetrics = {
	totalTime: number
	emaSpeed: number
}

const uploadMetricsInitial: UploadMetrics = {
	totalTime: 0,
	emaSpeed: 0,
}

export const uploadMetricsAtom = atom<UploadMetrics>(uploadMetricsInitial)

const EMA_ALPHA = 0.3
const MAX_DIRECT_UPLOAD_ATTEMPTS = 4
const getUploadProgressKey = (uploadId: number) => `direct:${uploadId}`

const textEncoder = new TextEncoder()

const encodeUtf8 = (value: string) => textEncoder.encode(value)

const sanitizeMultipartQuotedValue = (value: string) => value.replace(/["\r\n]/g, "_")

const concatBytesToArrayBuffer = (parts: Uint8Array[]): ArrayBuffer => {
	const byteLength = parts.reduce((sum, part) => sum + part.byteLength, 0)
	const output = new Uint8Array(byteLength)
	let offset = 0

	for (const part of parts) {
		output.set(part, offset)
		offset += part.byteLength
	}

	return output.buffer
}

const buildMultipartFormDataBody = (props: {
	fields: Record<string, string>
	fileFieldName: string
	filename: string
	fileType: string
	fileBytes: Uint8Array
}) => {
	const boundary = `----PixiesetPhotoEditor${Date.now().toString(36)}${Math.random()
		.toString(36)
		.slice(2)}`
	const parts: Uint8Array[] = []

	for (const [name, value] of Object.entries(props.fields)) {
		parts.push(
			encodeUtf8(
				`--${boundary}\r\n` +
					`Content-Disposition: form-data; name="${sanitizeMultipartQuotedValue(
						name,
					)}"\r\n\r\n` +
					`${value}\r\n`,
			),
		)
	}

	parts.push(
		encodeUtf8(
			`--${boundary}\r\n` +
				`Content-Disposition: form-data; name="${sanitizeMultipartQuotedValue(
					props.fileFieldName,
				)}"; filename="${sanitizeMultipartQuotedValue(props.filename)}"\r\n` +
				`Content-Type: ${props.fileType}\r\n\r\n`,
		),
		props.fileBytes,
		encodeUtf8(`\r\n--${boundary}--\r\n`),
	)

	const body = concatBytesToArrayBuffer(parts)

	return {
		body,
		contentType: `multipart/form-data; boundary=${boundary}`,
	}
}

const parseS3UploadResponse = (responseText: string) => {
	const parser = new DOMParser()
	const xmlDoc = parser.parseFromString(responseText, "text/xml")
	const error = xmlDoc.querySelector("Error")

	if (error) {
		throw new Error(
			error.querySelector("Message")?.textContent ??
				"Something went wrong. Please refresh and try again",
		)
	}

	const key = xmlDoc.querySelector("Key")

	if (!key?.textContent) {
		throw new Error("Something went wrong. Please refresh and try again")
	}

	return {
		url: key.textContent,
	}
}

type DirectUploadErrorDetails = {
	httpStatus: number
	responseTextLength: number
	responseFormat: "empty" | "other" | "s3-xml"
	s3ErrorCode?: string
	s3ErrorMessage?: string
	s3RequestId?: string
}

const directUploadErrorDetailsByError = new WeakMap<Error, DirectUploadErrorDetails>()

const getS3UploadErrorDetails = (responseText: string) => {
	if (!responseText) {
		return undefined
	}

	const xmlDoc = new DOMParser().parseFromString(responseText, "text/xml")
	const s3Error = xmlDoc.querySelector("Error")
	if (!s3Error) {
		return undefined
	}

	const getValue = (name: string) =>
		s3Error.querySelector(name)?.textContent?.trim().slice(0, 500)
	return {
		s3ErrorCode: getValue("Code"),
		s3ErrorMessage: getValue("Message"),
		s3RequestId: getValue("RequestId"),
	}
}

const getDirectUploadResponseDetails = (request: XMLHttpRequest): DirectUploadErrorDetails => {
	const responseText = request.responseText
	const s3ErrorDetails = getS3UploadErrorDetails(responseText)

	return {
		httpStatus: request.status,
		responseTextLength: responseText.length,
		responseFormat: s3ErrorDetails ? "s3-xml" : responseText ? "other" : "empty",
		...s3ErrorDetails,
	}
}

const makeXhrUploadError = (
	message: string,
	xhr: XMLHttpRequest,
	props?: {
		details?: DirectUploadErrorDetails
		isNetworkError?: boolean
		name?: string
	},
) => {
	const error = new Error(message)
	Object.assign(error, { request: xhr })
	directUploadErrorDetailsByError.set(
		error,
		props?.details ?? getDirectUploadResponseDetails(xhr),
	)

	if (props?.isNetworkError) {
		Object.assign(error, { isNetworkError: true })
	}
	if (props?.name) {
		error.name = props.name
	}

	return error
}

const makeUploadAbortError = () => {
	const error = new Error("Upload was cancelled")
	error.name = "AbortError"
	return error
}

const getErrorRequest = (error: unknown) => {
	if (
		typeof error !== "object" ||
		error === null ||
		!("request" in error) ||
		!(error.request instanceof XMLHttpRequest)
	) {
		return undefined
	}

	return error.request
}

const getErrorRequestStatus = (error: unknown) => getErrorRequest(error)?.status

const getDirectUploadErrorDetails = (error: unknown) => {
	return error instanceof Error ? directUploadErrorDetailsByError.get(error) : undefined
}

const isRetryableDirectUploadError = (error: unknown) => {
	if (isAbortError(error) || isBrowserOffline()) {
		return false
	}

	const status = getErrorRequestStatus(error)
	if (status === 408 || status === 429 || (typeof status === "number" && status >= 500)) {
		return true
	}

	return isNetworkConnectivityError(error)
}

export class ImageUploadService {
	private activeDirectUploads: Set<XMLHttpRequest> = new Set()
	private uploadBytesByFileId: Map<string, number> = new Map()
	private previousBytesUploaded: number = 0
	private previousTime: number | null = null
	private startTime: number | null = null
	private uploadSequence = 0

	constructor() {
		window.addEventListener("beforeunload", () => {
			this.cancelActiveDirectUploads()
		})
	}

	cancel() {
		this.cancelActiveDirectUploads()
	}

	reset() {
		this.cancelActiveDirectUploads()

		this.previousBytesUploaded = 0
		this.previousTime = null
		this.startTime = null
		this.uploadBytesByFileId.clear()
		store.set(uploadMetricsAtom, uploadMetricsInitial)
	}

	private updateEmaSpeed(currentSpeed: number) {
		const prev = store.get(uploadMetricsAtom).emaSpeed
		const next = prev === 0 ? currentSpeed : EMA_ALPHA * currentSpeed + (1 - EMA_ALPHA) * prev
		store.set(uploadMetricsAtom, (state) => ({ ...state, emaSpeed: next }))
	}

	private cancelActiveDirectUploads() {
		for (const xhr of this.activeDirectUploads) {
			xhr.abort()
		}
		this.activeDirectUploads.clear()
	}

	private captureUploadError(
		error: Error,
		fileInfo: { size: number; type: string },
		stage: string,
	) {
		if (!error) return

		const isOnline = typeof navigator !== "undefined" ? navigator.onLine : false
		const details = getDirectUploadErrorDetails(error)
		const sentryContext = {
			tags: {
				feature: "pixieset_upload",
				stage,
				isOnline: isOnline ? "true" : "false",
				...(details && {
					httpStatus: String(details.httpStatus),
					responseFormat: details.responseFormat,
				}),
				...(details?.s3ErrorCode && { s3ErrorCode: details.s3ErrorCode }),
			},
			extra: {
				fileType: fileInfo.type,
				fileSize: fileInfo.size,
				...(details?.responseFormat === "other" && {
					responseTextLength: details.responseTextLength,
				}),
				...(details?.s3ErrorMessage && { s3ErrorMessage: details.s3ErrorMessage }),
				...(details?.s3RequestId && { s3RequestId: details.s3RequestId }),
			},
		}

		if (isNetworkConnectivityError(error)) {
			Sentry.captureMessage("Pixieset upload network error", {
				level: "warning",
				...sentryContext,
				extra: {
					...sentryContext.extra,
					errorMessage: error instanceof Error ? error.message : String(error),
				},
			})
			return
		}

		Sentry.captureException(error, sentryContext)
	}

	private normalizeUploadError(error: unknown): Error {
		const fallbackMessage = "Upload failed with unknown error"

		if (error instanceof Error) {
			return error
		}

		if (typeof error === "string") {
			return new Error(error)
		}

		if (typeof error !== "object" || error === null) {
			return new Error(fallbackMessage)
		}

		const normalized = new Error(
			"message" in error && typeof error.message === "string"
				? error.message
				: fallbackMessage,
		)

		if ("name" in error && typeof error.name === "string") {
			normalized.name = error.name
		}
		if ("cause" in error) {
			Object.assign(normalized, { cause: error.cause })
		}
		if ("isNetworkError" in error && error.isNetworkError === true) {
			Object.assign(normalized, { isNetworkError: true })
		}
		if ("request" in error) {
			Object.assign(normalized, { request: error.request })
		}

		return normalized
	}

	private sendDirectUpload(props: {
		fileSize: number
		body: ArrayBuffer
		contentType: string
		endpoint: string
		uploadProgressKey: string
		signal?: AbortSignal
	}) {
		return new Promise<void>((resolve, reject) => {
			const { body, contentType, endpoint, fileSize, signal, uploadProgressKey } = props
			if (signal?.aborted) {
				reject(makeUploadAbortError())
				return
			}

			const xhr = new XMLHttpRequest()
			this.activeDirectUploads.add(xhr)

			const abortUpload = () => {
				xhr.abort()
			}

			const cleanup = () => {
				this.activeDirectUploads.delete(xhr)
				signal?.removeEventListener("abort", abortUpload)
				xhr.upload.onprogress = null
				xhr.onload = null
				xhr.onerror = null
				xhr.onabort = null
				xhr.ontimeout = null
			}

			signal?.addEventListener("abort", abortUpload, { once: true })

			xhr.upload.onprogress = (event) => {
				const bytesUploaded = Math.min(fileSize, event.loaded)
				this.uploadBytesByFileId.set(uploadProgressKey, bytesUploaded)
				this.updateUploadSpeedThrottled()
			}

			xhr.onload = () => {
				cleanup()

				if (xhr.status < 200 || xhr.status >= 300) {
					const details = getDirectUploadResponseDetails(xhr)
					const s3ErrorReason = [details.s3ErrorCode, details.s3ErrorMessage]
						.filter(Boolean)
						.join(": ")
					reject(
						makeXhrUploadError(
							`Upload failed with status ${xhr.status || "unknown"}${
								s3ErrorReason ? `: ${s3ErrorReason}` : ""
							}`,
							xhr,
							{ details },
						),
					)
					return
				}

				try {
					parseS3UploadResponse(xhr.responseText)
					resolve()
				} catch (error) {
					reject(error)
				}
			}

			xhr.onerror = () => {
				cleanup()
				reject(
					makeXhrUploadError(
						"This looks like a network error, the endpoint might be blocked by an internet provider or a firewall.",
						xhr,
						{ isNetworkError: true },
					),
				)
			}

			xhr.onabort = () => {
				cleanup()
				reject(
					makeXhrUploadError("Upload was cancelled", xhr, {
						isNetworkError: true,
						name: "AbortError",
					}),
				)
			}

			xhr.ontimeout = () => {
				cleanup()
				reject(
					makeXhrUploadError("Upload timed out", xhr, {
						isNetworkError: true,
						name: "TimeoutError",
					}),
				)
			}

			xhr.open("POST", endpoint)
			xhr.setRequestHeader("Content-Type", contentType)
			xhr.send(body)
		})
	}

	private async sendDirectUploadWithRetries(props: {
		fileSize: number
		body: ArrayBuffer
		contentType: string
		endpoint: string
		uploadProgressKey: string
		signal?: AbortSignal
	}) {
		let lastError: unknown

		for (let attempt = 1; attempt <= MAX_DIRECT_UPLOAD_ATTEMPTS; attempt += 1) {
			try {
				await this.sendDirectUpload(props)
				return
			} catch (error) {
				lastError = error
				const shouldRetry =
					attempt < MAX_DIRECT_UPLOAD_ATTEMPTS && isRetryableDirectUploadError(error)

				if (!shouldRetry) {
					throw error
				}

				this.uploadBytesByFileId.set(props.uploadProgressKey, 0)
				await wait(BASE_NETWORK_RETRY_DELAY_MS * 2 ** (attempt - 1))
			}
		}

		throw lastError
	}

	uploadImageBytes(
		uploadData: EncodedJPEGData,
		key: string,
		filename: string,
		uploadRequestResponse: BeforeUploadResponseDTO,
		onComplete: (filename: string) => void,
		onError: (filename: string, error?: unknown) => void,
		options?: { signal?: AbortSignal },
	): Promise<void> {
		if (options?.signal?.aborted) {
			return Promise.reject(makeUploadAbortError())
		}

		if (!this.startTime) {
			this.startTime = Date.now()
		}

		const uploadId = ++this.uploadSequence
		const uploadProgressKey = getUploadProgressKey(uploadId)
		const fileSize = uploadData.bytes.byteLength
		const fileType = uploadData.type
		const fields = {
			key,
			acl: "private",
			"Content-Type": fileType,
			AWSAccessKeyId: uploadRequestResponse.ups.akid,
			policy: uploadRequestResponse.ups.p,
			signature: uploadRequestResponse.ups.s,
			success_action_status: "201",
		}
		const multipartBody = buildMultipartFormDataBody({
			fields,
			fileFieldName: "file",
			filename,
			fileType,
			fileBytes: uploadData.bytes,
		})

		if (options?.signal?.aborted) {
			return Promise.reject(makeUploadAbortError())
		}

		return this.sendDirectUploadWithRetries({
			fileSize,
			body: multipartBody.body,
			contentType: multipartBody.contentType,
			endpoint: uploadRequestResponse.ups.ep,
			uploadProgressKey,
			signal: options?.signal,
		})
			.then(() => {
				this.uploadBytesByFileId.set(uploadProgressKey, fileSize)
				this.updateTotalTime()
				onComplete(filename)
			})
			.catch((error) => {
				const normalizedError = this.normalizeUploadError(error)
				this.updateTotalTime()
				if (!isAbortError(normalizedError)) {
					this.captureUploadError(
						normalizedError,
						{
							size: fileSize,
							type: fileType,
						},
						"direct_upload_failed",
					)
				}
				onError(filename, normalizedError)
				throw normalizedError
			})
	}

	private updateUploadSpeed() {
		const totalBytesUploaded = Array.from(this.uploadBytesByFileId.values()).reduce(
			(sum, bytesUploaded) => sum + bytesUploaded,
			0,
		)

		const currentTime = Date.now()

		if (this.previousTime && totalBytesUploaded > this.previousBytesUploaded) {
			const elapsedTimeInSeconds = (currentTime - this.previousTime) / 1000

			const bytesUploadedSinceLastTick = totalBytesUploaded - this.previousBytesUploaded
			const speed = bytesUploadedSinceLastTick / elapsedTimeInSeconds
			this.updateEmaSpeed(speed)
		}

		this.updateTotalTime(currentTime)

		this.previousBytesUploaded = totalBytesUploaded
		this.previousTime = currentTime
	}

	private updateTotalTime(currentTime = Date.now()) {
		if (this.startTime === null) {
			return
		}

		const totalElapsedTimeInSeconds = (currentTime - this.startTime) / 1000
		store.set(uploadMetricsAtom, (state) => ({
			...state,
			totalTime: totalElapsedTimeInSeconds,
		}))
	}

	@throttled(1000)
	private updateUploadSpeedThrottled() {
		this.updateUploadSpeed()
	}
}
